import React, { useState } from 'react';
import { Card, Upload, Button, Select, message, Tabs, Alert, Row, Col, Statistic, Table } from 'antd';
import { UploadOutlined, InboxOutlined, BarChartOutlined } from '@ant-design/icons';
import { advertisingAPI } from '../../../services/advertisingAPI';

const { Dragger } = Upload;
const { Option } = Select;

const AdvertisingDashboard: React.FC = () => {
    const [uploading, setUploading] = useState(false);
    const [reportType, setReportType] = useState<'search_term' | 'campaign_performance' | 'advertised_product'>('search_term');

    const handleUpload = async (file: File) => {
        setUploading(true);
        try {
            const res = await advertisingAPI.uploadReport({
                file,
                report_type: reportType
            });
            message.success(`上传成功! 处理了 ${res.data.processed} 条数据`);
        } catch (error: any) {
            message.error('上传失败: ' + (error.response?.data?.error || error.message));
        } finally {
            setUploading(false);
        }
    };

    const uploadProps = {
        name: 'file',
        multiple: false,
        beforeUpload: (file: File) => {
            handleUpload(file);
            return false; // 阻止自动上传
        },
        showUploadList: false,
    };

    return (
        <div style={{ padding: 24 }}>
            <h1 style={{ marginBottom: 24 }}>广告数据中心 (Amazon Ads Manual Import)</h1>

            <Row gutter={24}>
                {/* 左侧：上传区域 */}
                <Col span={8}>
                    <Card title="导入报表" bordered={false} style={{ height: '100%' }}>
                        <div style={{ marginBottom: 16 }}>
                            <span style={{ marginRight: 8 }}>报表类型:</span>
                            <Select
                                value={reportType}
                                onChange={setReportType}
                                style={{ width: 220 }}
                            >
                                <Option value="search_term">Search Term Report (SD/SP)</Option>
                                <Option value="advertised_product">Advertised Product Report</Option>
                                <Option value="campaign_performance">Campaign Performance Report</Option>
                            </Select>
                        </div>

                        <Dragger {...uploadProps} disabled={uploading}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                            <p className="ant-upload-hint">
                                支持 .xlsx 或 .csv 格式的亚马逊后台导出报表
                            </p>
                        </Dragger>

                        {uploading && <div style={{ marginTop: 16, textAlign: 'center' }}>文件处理中，请稍候...</div>}

                        <Alert
                            message="操作指南"
                            description={
                                <ul style={{ paddingLeft: 20 }}>
                                    <li>请直接使用 Amazon Advertising 后台导出的原始报表</li>
                                    <li>支持 "Sponsored Products Search Term Report"</li>
                                    <li>文件上传后会自动解析并更新广告活动、广告组及绩效数据</li>
                                </ul>
                            }
                            type="info"
                            showIcon
                            style={{ marginTop: 24 }}
                        />
                    </Card>
                </Col>

                {/* 右侧：数据概览 (Placeholder) */}
                <Col span={16}>
                    <Card title="今日概览 (示例数据)" bordered={false}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Statistic title="总花费 (Spend)" value={1256.40} prefix="$" precision={2} />
                            </Col>
                            <Col span={6}>
                                <Statistic title="总销售额 (Sales)" value={5890.00} prefix="$" precision={2} />
                            </Col>
                            <Col span={6}>
                                <Statistic title="ACOS" value={21.33} suffix="%" precision={2} valueStyle={{ color: '#3f8600' }} />
                            </Col>
                            <Col span={6}>
                                <Statistic title="ROAS" value={4.68} precision={2} />
                            </Col>
                        </Row>

                        <div style={{ marginTop: 32, height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', color: '#999' }}>
                            <BarChartOutlined style={{ fontSize: 48, marginRight: 16 }} />
                            <span>图表开发中: Spend vs Sales 趋势图</span>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default AdvertisingDashboard;
